import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Bomb } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

// grid: # hard wall, S soft block, . empty
const COLS = 11, ROWS = 9, CELL = 26, W = COLS * CELL, H = ROWS * CELL;

export default function Bomber() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const build = () => {
    const grid = [];
    for (let r = 0; r < ROWS; r++) { grid[r] = []; for (let c = 0; c < COLS; c++) { if (r === 0 || c === 0 || r === ROWS - 1 || c === COLS - 1 || (r % 2 === 0 && c % 2 === 0)) grid[r][c] = "#"; else if ((r <= 2 && c <= 2)) grid[r][c] = "."; else grid[r][c] = Math.random() < 0.55 ? "S" : "."; } }
    return grid;
  };
  const start = () => {
    const grid = build();
    const enemies = [{ x: COLS - 2, y: ROWS - 2 }, { x: COLS - 2, y: 1 }, { x: 1, y: ROWS - 2 }].map((e) => {
      grid[e.y][e.x] = ".";
      // open a small pocket around each enemy so it isn't boxed in by soft blocks (they'd look frozen otherwise)
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = e.x + dx, ny = e.y + dy;
        if (grid[ny] && grid[ny][nx] === "S") grid[ny][nx] = ".";
      }
      return { ...e, mv: 0 };
    });
    g.current = { grid, px: 1, py: 1, bombs: [], blasts: [], enemies, etick: 0 };
    scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep();
  };
  const canWalk = (c, r) => c >= 0 && c < COLS && r >= 0 && r < ROWS && g.current.grid[r][c] === ".";
  const step = (dx, dy) => { const s = g.current; if (!s || status !== "playing") return; if (canWalk(s.px + dx, s.py + dy)) { s.px += dx; s.py += dy; } };
  const drop = () => { const s = g.current; if (!s || status !== "playing") return; if (s.bombs.some((b) => b.x === s.px && b.y === s.py)) return; s.bombs.push({ x: s.px, y: s.py, t: 90 }); sfx.click(); };

  useEffect(() => {
    const kd = (e) => {
      if (["ArrowUp", "w"].includes(e.key)) step(0, -1);
      else if (["ArrowDown", "s"].includes(e.key)) step(0, 1);
      else if (["ArrowLeft", "a"].includes(e.key)) step(-1, 0);
      else if (["ArrowRight", "d"].includes(e.key)) step(1, 0);
      else if (e.key === " ") drop();
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      const s = g.current; if (!s.grid) return;
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) { const t = s.grid[r][c]; ctx.fillStyle = t === "#" ? "#555" : t === "S" ? "#8a5a2b" : "#2b6e3f"; ctx.fillRect(c * CELL, r * CELL, CELL, CELL); ctx.fillStyle = "rgba(0,0,0,.15)"; ctx.fillRect(c * CELL, r * CELL, CELL, 3); }
      s.blasts.forEach((b) => { ctx.fillStyle = "#f59e0b"; ctx.fillRect(b.x * CELL + 2, b.y * CELL + 2, CELL - 4, CELL - 4); });
      s.bombs.forEach((b) => { ctx.fillStyle = b.t % 20 < 10 ? "#111" : "#c1121f"; ctx.beginPath(); ctx.arc(b.x * CELL + CELL / 2, b.y * CELL + CELL / 2, CELL / 2 - 4, 0, 7); ctx.fill(); });
      s.enemies.forEach((e) => { ctx.fillStyle = "#a78bfa"; ctx.fillRect(e.x * CELL + 4, e.y * CELL + 4, CELL - 8, CELL - 8); });
      ctx.fillStyle = "#22d3ee"; ctx.fillRect(s.px * CELL + 4, s.py * CELL + 4, CELL - 8, CELL - 8);
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const explode = (s, bx, by) => {
      const cells = [[bx, by]];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { for (let i = 1; i <= 2; i++) { const c = bx + dx * i, r = by + dy * i; if (c < 0 || c >= COLS || r < 0 || r >= ROWS || s.grid[r][c] === "#") break; cells.push([c, r]); if (s.grid[r][c] === "S") { s.grid[r][c] = "."; scoreRef.current += 5; setScore(scoreRef.current); break; } } }
      cells.forEach(([c, r]) => s.blasts.push({ x: c, y: r, t: 18 }));
      sfx.explode();
    };
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      s.bombs = s.bombs.filter((b) => { b.t--; if (b.t <= 0) { explode(s, b.x, b.y); return false; } return true; });
      s.blasts = s.blasts.filter((b) => { b.t--; return b.t > 0; });
      s.etick++;
      if (s.etick >= 22) {
        s.etick = 0;
        for (const e of s.enemies) {
          const opts = [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([dx, dy]) => canWalk(e.x + dx, e.y + dy));
          if (!opts.length) continue;
          let p;
          if (Math.random() < 0.6) { // chase the player most of the time
            opts.sort((a, b) => (Math.abs(e.x + a[0] - s.px) + Math.abs(e.y + a[1] - s.py)) - (Math.abs(e.x + b[0] - s.px) + Math.abs(e.y + b[1] - s.py)));
            p = opts[0];
          } else { p = opts[(Math.random() * opts.length) | 0]; }
          e.x += p[0]; e.y += p[1];
        }
      }
      // blast kills
      s.enemies = s.enemies.filter((e) => { if (s.blasts.some((b) => b.x === e.x && b.y === e.y)) { scoreRef.current += 20; setScore(scoreRef.current); return false; } return true; });
      const dead = s.blasts.some((b) => b.x === s.px && b.y === s.py) || s.enemies.some((e) => e.x === s.px && e.y === s.py);
      if (dead) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
      if (s.enemies.length === 0) { cancelAnimationFrame(raf); sfx.win(); setStatus("win"); return; }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Score" value={score} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[286px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-2">
            {status === "over" && <AResult win={false} text={`Boom! · ${score}`} testId="bomber-over" />}
            {status === "win" && <AResult win={true} text={`Cleared! · ${score}`} testId="bomber-win" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="grid w-max grid-cols-3 gap-1">
          <span /><PadButton testId="bomber-up" onPress={() => step(0, -1)}><ArrowUp className="h-5 w-5" /></PadButton><span />
          <PadButton testId="bomber-left" onPress={() => step(-1, 0)}><ArrowLeft className="h-5 w-5" /></PadButton>
          <span />
          <PadButton testId="bomber-right" onPress={() => step(1, 0)}><ArrowRight className="h-5 w-5" /></PadButton>
          <span /><PadButton testId="bomber-down" onPress={() => step(0, 1)}><ArrowDown className="h-5 w-5" /></PadButton><span />
        </div>
        <PadButton testId="bomber-bomb" onPress={drop} className="h-14 w-14 bg-kirana-red text-white"><Bomb className="h-6 w-6" /></PadButton>
      </div>
    </div>
  );
}
